import React from 'react';
import * as stylex from '@stylexjs/stylex';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorNeutralInverse, ColorBrand } from '../theme/Colors.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight } from '../theme/Fonts.stylex';
import { Icon, type IconName } from '../images-and-icons/Icon';

// MARK: exported types
// ====================
type Size = 'large' | 'medium' | 'small';
type Variant = 'solid' | 'ghost' | 'outline';
type CustomToneParams = {
  borderColor?: string;
  backgroundColor?: string;
  foregroundColor?: string;
};
type Tone = 'brand' | 'neutral' | {
  NAME: 'custom';
  VAL: CustomToneParams;
};
type Shape = 'circle' | 'square';
type Content = {
  NAME: 'icon';
  VAL: IconName;
} | {
  NAME: 'image';
  VAL: string;
} | {
  NAME: 'initials';
  VAL: [number, number];
};

// MARK: root
// ==========
const customToneVal = (tone: Tone): CustomToneParams | undefined => typeof tone === 'object' && tone.NAME === 'custom' ? tone.VAL : undefined;
const toneName = (tone: Tone): string => typeof tone === 'object' ? tone.NAME : tone;
const iconSizeFromSize = (size: Size): number => {
  switch (size) {
    case 'large':
      return 18;
    case 'medium':
      return 12;
    case 'small':
      return 12;
  }
};
const foregroundColor = (tone: Tone, variant: Variant): string => {
  if (variant === 'solid') return ColorNeutralInverse.textAccent;
  switch (toneName(tone)) {
    case 'brand':
      return ColorBrand.textDefault;
    case 'neutral':
      return ColorNeutral.textAccent;
    case 'custom':
      return customToneVal(tone)?.foregroundColor ?? ColorNeutral.textAccent;
  }
  return ColorNeutral.textAccent;
};
const iconFillColor = (tone: Tone, variant: Variant): string => {
  if (variant === 'solid') return ColorNeutralInverse.iconAccent;
  switch (toneName(tone)) {
    case 'brand':
      return ColorBrand.iconDefault;
    case 'neutral':
      return ColorNeutral.iconAccent;
    case 'custom':
      return customToneVal(tone)?.foregroundColor ?? ColorNeutral.iconAccent;
  }
  return ColorNeutral.iconAccent;
};

// MARK: style:props
// =================
const styleProps = (variant: Variant, tone: Tone, size: Size, shape: Shape) => {
  const custom = customToneVal(tone);
  return stylex.props(styles.root, sizeStyles[size], shape === 'circle' ? styles.circle : styles.square, variant === 'outline' && styles.outline, variant === 'ghost' && styles.ghost, toneStyles[`${variant}_${toneName(tone)}` as keyof typeof toneStyles], custom && dynamicStyles.customTone(custom.backgroundColor ?? 'transparent', custom.borderColor ?? 'transparent'));
};
const initialsStyleProps = (variant: Variant, tone: Tone, size: Size) => stylex.props(styles.initials, sizeStyles[`${size}Font` as keyof typeof sizeStyles], dynamicStyles.foreground(foregroundColor(tone, variant)));
type AvatarProps = {
  content: Content;
  variant?: Variant;
  tone?: Tone;
  size?: Size;
  shape?: Shape;
};
function AvatarComponent({
  content,
  variant = 'solid',
  tone = 'neutral',
  size = 'medium',
  shape = 'circle'
}: AvatarProps) {
  const iconSize = iconSizeFromSize(size);
  return <div {...styleProps(variant, tone, size, shape)}>
      {content.NAME === 'icon' && <Icon name={content.VAL} fill={iconFillColor(tone, variant)} width={iconSize} height={iconSize} />}
      {content.NAME === 'image' && <img src={content.VAL} alt="avatar" className="width-xh8yej3 height-x5yr21d objectFit-xl1xv1r" data-style-src="wino-gc:src/resources/feedback-indicators/Avatar.tsx:238" />}
      {content.NAME === 'initials' && <span {...initialsStyleProps(variant, tone, size)}>
          {String.fromCharCode(content.VAL[0])}
          {String.fromCharCode(content.VAL[1])}
        </span>}
    </div>;
}
const Avatar = React.memo(AvatarComponent) as typeof AvatarComponent;
export { Avatar, type AvatarProps };

// MARK: style:size
const sizeStyles = {
  large: {
    "width-kzqmXN": "width-xgd8bvy",
    "height-kZKoxP": "height-x1fgtraw",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:144"
  },
  medium: {
    "width-kzqmXN": "width-xw4jnvo",
    "height-kZKoxP": "height-x1qx5ct2",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:148"
  },
  small: {
    "width-kzqmXN": "width-x1kky2od",
    "height-kZKoxP": "height-xlup9mm",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:152"
  },
  largeFont: {
    "fontSize-kGuDYH": "fontSize-x1r9ey3o",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:156"
  },
  mediumFont: {
    "fontSize-kGuDYH": "fontSize-xeppc8w",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:159"
  },
  smallFont: {
    "fontSize-kGuDYH": "fontSize-xeppc8w",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:162"
  }
};

// MARK: style:tone
const toneStyles = {
  solid_brand: {
    "backgroundColor-kWkggS": "backgroundColor-x1ryq1pb",
    "borderColor-kVAM5u": "borderColor-x39ameu",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:169"
  },
  solid_neutral: {
    "backgroundColor-kWkggS": "backgroundColor-xug7tn3",
    "borderColor-kVAM5u": "borderColor-x1qnz5iy",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:173"
  },
  solid_custom: {
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:177"
  },
  ghost_brand: {
    "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:178"
  },
  ghost_neutral: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:181"
  },
  ghost_custom: {
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:184"
  },
  outline_brand: {
    "borderColor-kVAM5u": "borderColor-x19mpen5",
    "color-kMwMTN": "color-x1mzc8w9",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:185"
  },
  outline_neutral: {
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:189"
  },
  outline_custom: {
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:193"
  }
};

// MARK: style:dynamic
const dynamicStyles = {
  customTone: (backgroundColor: string, borderColor: string) => [{
    "backgroundColor-kWkggS": backgroundColor != null ? "backgroundColor-xl8spv7" : backgroundColor,
    "borderColor-kVAM5u": borderColor != null ? "borderColor-x6w5c0t" : borderColor,
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:198"
  }, {
    "--x-backgroundColor": backgroundColor != null ? backgroundColor : undefined,
    "--x-borderColor": borderColor != null ? borderColor : undefined
  }],
  foreground: (color: string) => [{
    "color-kMwMTN": color != null ? "color-x14rh7hd" : color,
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:202"
  }, {
    "--x-color": color != null ? color : undefined
  }]
};

// MARK: style
// ===========
const styles = {
  root: {
    "display-k1xSpc": "display-x3nfvp2",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "justifyContent-kjj79g": "justifyContent-xl56j7k",
    "overflow-kVQacm": "overflow-xb3r6kr",
    "boxSizing-kB7OPa": "boxSizing-x9f619",
    "fontFamily-kMv6JI": "fontFamily-x111x8ze",
    "fontWeight-k63SB2": "fontWeight-x168tpfk",
    "borderWidth-kMzoRj": "borderWidth-xc342km",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    "borderColor-kVAM5u": "borderColor-x9r1u3d",
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    "userSelect-kfSwDN": "userSelect-x87ps6o",
    "flexShrink-kmuXW": "flexShrink-x2lah0s",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:210"
  },
  circle: {
    "borderRadius-kaIpWk": "borderRadius-xj42ba9",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:225"
  },
  square: {
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:228"
  },
  outline: {
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:231"
  },
  ghost: {
    "borderWidth-kMzoRj": "borderWidth-xc342km",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:235"
  },
  initials: {
    "lineHeight-kLWn49": "lineHeight-xo5v014",
    "textTransform-kP9fke": "textTransform-xtvhhri",
    $$css: "wino-gc:src/resources/feedback-indicators/Avatar.tsx:243"
  }
};